import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Download, Calendar, BookOpen, Eye, Archive } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PDFViewer } from './ui/pdf-viewer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

interface Magazine {
  id: string;
  title: string;
  year: number;
  start_month: number;
  end_month: number;
  pdf_path: string;
  published: boolean;
  created_at: string;
}

const NewsletterManager = () => {
  const [magazines, setMagazines] = useState<Magazine[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [viewingPdf, setViewingPdf] = useState<{ url: string; title: string } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadMagazines();
  }, []);

  const loadMagazines = async () => {
    try {
      const { data, error } = await supabase
        .from('magazines')
        .select('*')
        .eq('published', true)
        .order('year', { ascending: false })
        .order('start_month', { ascending: false });

      if (error) throw error;
      setMagazines(data || []);
    } catch (error) {
      console.error('Error loading magazines:', error);
      toast({
        title: "Error",
        description: "Failed to load magazines",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getMonthRange = (startMonth: number, endMonth: number) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[startMonth - 1]}-${months[endMonth - 1]}`;
  };

  const viewMagazine = async (magazine: Magazine) => {
    try {
      // Download as Blob to avoid any cross-origin or range request issues with PDF.js
      const { data, error } = await supabase.storage
        .from('general-media')
        .download(magazine.pdf_path);

      if (error) throw error;

      const blobUrl = URL.createObjectURL(data);
      setViewingPdf({ url: blobUrl, title: magazine.title });
    } catch (error) {
      console.error('Error viewing magazine:', error);
      toast({
        title: "Error",
        description: "Failed to view magazine PDF",
        variant: "destructive",
      });
    }
  };

  const downloadMagazine = async (magazine: Magazine) => {
    try {
      const { data, error } = await supabase.storage
        .from('general-media')
        .download(magazine.pdf_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${magazine.title || 'magazine'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Magazine downloaded successfully",
      });
    } catch (error) {
      console.error('Error downloading magazine:', error);
      toast({
        title: "Error",
        description: "Failed to download magazine",
        variant: "destructive",
      });
    }
  };

  // Sort: latest year first, then latest quarter (start_month) first
  const sortedMagazines = [...magazines].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.start_month - a.start_month;
  });

  const availableYears = [...new Set(sortedMagazines.map(m => m.year))].sort((a, b) => b - a);
  const latestYear = availableYears[0] ?? new Date().getFullYear();
  const archiveYears = availableYears.filter(y => y !== latestYear);

  const latestMagazines = sortedMagazines.filter(m => m.year === latestYear);
  const archiveMagazines = sortedMagazines.filter(m => m.year !== latestYear);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-muted-foreground">Loading magazines...</div>
      </div>
    );
  }

  const latestMagazineId = sortedMagazines[0]?.id;

  const renderMagazineCard = (magazine: Magazine) => {
    const isLatest = magazine.id === latestMagazineId;
    return (
      <Card
        key={magazine.id}
        className={`relative hover:shadow-lg transition-shadow ${
          isLatest ? "ring-2 ring-primary shadow-xl" : ""
        }`}
      >
        {isLatest && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <Button
              size="sm"
              className="h-7 rounded-full px-4 text-xs font-bold uppercase tracking-wide shadow-md pointer-events-none"
            >
              Latest
            </Button>
          </div>
        )}
        <CardHeader className={isLatest ? "pt-6" : undefined}>
          <CardTitle className="text-lg">{magazine.title}</CardTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{magazine.year} - {getMonthRange(magazine.start_month, magazine.end_month)}</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button onClick={() => viewMagazine(magazine)} className="flex-1" variant="default">
              <Eye className="h-4 w-4 mr-2" /> View
            </Button>
            <Button onClick={() => downloadMagazine(magazine)} variant="outline" className="flex-1">
              <Download className="h-4 w-4 mr-2" /> Download
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };


  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Quarterly Magazines
          </CardTitle>
        </CardHeader>
        <CardContent>
          {magazines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No magazines available yet.</div>
          ) : (
            <Tabs defaultValue="latest" className="w-full">
              <TabsList>
                <TabsTrigger value="latest">{latestYear} (Latest)</TabsTrigger>
                {archiveYears.length > 0 && (
                  <TabsTrigger value="archive">
                    <Archive className="h-4 w-4 mr-1.5" /> Archive
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="latest" className="mt-6">
                {latestMagazines.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No magazines for {latestYear} yet.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {latestMagazines.map(renderMagazineCard)}
                  </div>
                )}
              </TabsContent>

              {archiveYears.length > 0 && (
                <TabsContent value="archive" className="mt-6">
                  <Tabs defaultValue={String(archiveYears[0])} className="w-full">
                    <TabsList className="flex-wrap h-auto">
                      {archiveYears.map(y => (
                        <TabsTrigger key={y} value={String(y)}>{y}</TabsTrigger>
                      ))}
                    </TabsList>
                    {archiveYears.map(y => (
                      <TabsContent key={y} value={String(y)} className="mt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {archiveMagazines.filter(m => m.year === y).map(renderMagazineCard)}
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                </TabsContent>
              )}
            </Tabs>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Dialog */}
      <Dialog
        open={!!viewingPdf}
        onOpenChange={(open) => {
          if (open) return;
          if (viewingPdf?.url?.startsWith('blob:')) {
            URL.revokeObjectURL(viewingPdf.url);
          }
          setViewingPdf(null);
        }}
      >
        <DialogContent className="max-w-6xl h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>{viewingPdf?.title}</DialogTitle>
            <DialogDescription>PDF preview. Use arrows to navigate pages or download the file.</DialogDescription>
          </DialogHeader>
          {viewingPdf && (
            <div className="h-[calc(90vh-80px)]">
              <PDFViewer
                url={viewingPdf.url}
                fileName={`${viewingPdf.title}.pdf`}
                dialogOpen={!!viewingPdf}
                debugLabel="KIT PDF Viewer"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsletterManager;
